import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { env } from '@/lib/env';
import { useAuthStore } from '@/state/authStore';
import {
  login,
  respondNewPassword,
  forgotPassword,
  confirmForgotPassword,
  CognitoError,
} from './cognitoAuth';
import type { AuthTokens } from './cognitoAuth';

const PASSWORD_RULE_TEXT = '8文字以上、大文字・小文字・数字・記号（例: @）をそれぞれ1文字以上含めてください。';

type Phase = 'login' | 'new_password' | 'forgot' | 'forgot_confirm';

type LocalLoginRole = 'admin' | 'feedbacker1' | 'feedbacker2';

const LOCAL_LOGIN_USERS: Record<LocalLoginRole, {
  id: string;
  email: string;
  name: string;
  label: string;
  isFeedbacker: boolean;
  isAdmin: boolean;
}> = {
  admin: {
    id: '00000000-0000-4000-a000-000000000001',
    email: 'qa-admin@test.local',
    name: 'QA Admin',
    label: '管理者',
    isFeedbacker: true,
    isAdmin: true,
  },
  feedbacker1: {
    id: '00000000-0000-4000-a000-000000000003',
    email: 'qa-feedbacker1@test.local',
    name: 'QA Feedbacker 1',
    label: 'フィードバッカー1',
    isFeedbacker: true,
    isAdmin: false,
  },
  feedbacker2: {
    id: '00000000-0000-4000-a000-000000000004',
    email: 'qa-feedbacker2@test.local',
    name: 'QA Feedbacker 2',
    label: 'フィードバッカー2',
    isFeedbacker: true,
    isAdmin: false,
  },
};

function base64Url(value: unknown): string {
  return btoa(JSON.stringify(value))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function localAuthTokens(role: LocalLoginRole): AuthTokens {
  const user = LOCAL_LOGIN_USERS[role];
  const token = [
    base64Url({ alg: 'none', typ: 'JWT' }),
    base64Url({
      e2e_auth: 'local',
      sub: `local-e2e-${role}`,
      email: user.email,
      name: user.name,
      'custom:user_id': user.id,
      'custom:email': user.email,
      'custom:name': user.name,
      'custom:is_feedbacker': String(user.isFeedbacker),
      'custom:is_admin': String(user.isAdmin),
      exp: Math.floor(Date.now() / 1000) + 24 * 60 * 60,
    }),
    'local',
  ].join('.');
  return {
    access_token: token,
    id_token: token,
    refresh_token: 'local-e2e-refresh-token',
  };
}

type PasswordInputProps = {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder: string;
  autoComplete: string;
};

function PasswordInput({
  value,
  onChange,
  onKeyDown,
  placeholder,
  autoComplete,
}: PasswordInputProps) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative mt-1">
      <input
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="w-full rounded-lg border border-gray-300 py-2.5 pl-4 pr-11 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setVisible((current) => !current)}
        className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label={visible ? 'パスワードを非表示' : 'パスワードを表示'}
        title={visible ? 'パスワードを非表示' : 'パスワードを表示'}
      >
        {visible ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
}

function EyeIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 3l18 18" />
      <path d="M10.6 10.6A2 2 0 0 0 12 14a2 2 0 0 0 1.4-.6" />
      <path d="M9.9 4.4A9.5 9.5 0 0 1 12 4c6.5 0 10 8 10 8a17.6 17.6 0 0 1-3.1 4.3" />
      <path d="M6.6 6.6C3.6 8.7 2 12 2 12s3.5 8 10 8a9.7 9.7 0 0 0 4.4-1.1" />
    </svg>
  );
}

export function LoginPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setTokens = useAuthStore((s) => s.setTokens);
  const [params] = useSearchParams();

  const [phase, setPhase] = useState<Phase>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [session, setSession] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(params.get('error') ?? '');
  const [info, setInfo] = useState('');

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const clearMessages = () => { setError(''); setInfo(''); };

  const handleLocalLogin = (role: LocalLoginRole) => {
    clearMessages();
    completeLogin(localAuthTokens(role));
  };

  const handleLogin = async () => {
    if (!email || !password) return;
    setLoading(true);
    clearMessages();
    try {
      const result = await login(email, password);
      if (result.type === 'new_password_required') {
        setPhase('new_password');
        setSession(result.session);
        setPassword('');
      } else {
        completeLogin(result.tokens);
      }
    } catch (e) {
      if (e instanceof CognitoError) {
        setError(translateError(e.code));
      } else {
        setError('ログインに失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async () => {
    if (!newPassword) return;
    if (newPassword !== newPasswordConfirm) {
      setError('パスワードが一致しません');
      return;
    }
    setLoading(true);
    clearMessages();
    try {
      const tokens = await respondNewPassword(email, newPassword, session);
      completeLogin(tokens);
    } catch (e) {
      if (e instanceof CognitoError) {
        setError(translateError(e.code));
      } else {
        setError('パスワード設定に失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('メールアドレスを入力してください');
      return;
    }
    setLoading(true);
    clearMessages();
    try {
      await forgotPassword(email);
      setPhase('forgot_confirm');
      setInfo('確認コードをメールに送信しました。メールを確認してください。');
    } catch (e) {
      if (e instanceof CognitoError) {
        setError(translateError(e.code));
      } else {
        setError('リセットメールの送信に失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async () => {
    if (!resetCode || !newPassword) return;
    if (newPassword !== newPasswordConfirm) {
      setError('パスワードが一致しません');
      return;
    }
    setLoading(true);
    clearMessages();
    try {
      await confirmForgotPassword(email, resetCode, newPassword);
      setInfo('パスワードをリセットしました。新しいパスワードでログインしてください。');
      setPhase('login');
      setPassword('');
      setNewPassword('');
      setNewPasswordConfirm('');
      setResetCode('');
    } catch (e) {
      if (e instanceof CognitoError) {
        setError(translateError(e.code));
      } else {
        setError('パスワードリセットに失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };

  const completeLogin = (tokens: AuthTokens) => {
    setTokens(tokens);
  };

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter') action();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="w-full max-w-md rounded-xl bg-white p-8 shadow-lg">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Delphi</h1>
          <p className="mt-1 text-sm text-gray-500">コーチ育成支援システム</p>
        </div>

        {error && (
          <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}
        {info && (
          <div className="mt-4 rounded-lg bg-green-50 p-3 text-sm text-green-700">{info}</div>
        )}

        {env.VITE_E2E_AUTH_MODE === 'local' && (
          <div className="mt-6 rounded-lg border border-blue-200 bg-blue-50 p-3">
            <p className="text-xs font-semibold text-blue-800">ローカル確認用ログイン</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-3">
              {(Object.keys(LOCAL_LOGIN_USERS) as LocalLoginRole[]).map((role) => {
                const user = LOCAL_LOGIN_USERS[role];
                return (
                  <button
                    key={role}
                    type="button"
                    onClick={() => handleLocalLogin(role)}
                    className="rounded-md border border-blue-200 bg-white px-3 py-2 text-left text-xs shadow-sm hover:bg-blue-100"
                  >
                    <span className="block font-bold text-blue-900">{user.label}</span>
                    <span className="mt-0.5 block break-all text-blue-700">{user.email}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ログインフォーム */}
        {phase === 'login' && (
          <div className="mt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">メールアドレス</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, handleLogin)}
                placeholder="example@test.com"
                autoComplete="email"
                className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">パスワード</label>
              <PasswordInput
                value={password}
                onChange={setPassword}
                onKeyDown={(e) => handleKeyDown(e, handleLogin)}
                placeholder="パスワードを入力"
                autoComplete="current-password"
              />
            </div>
            <button
              type="button"
              onClick={handleLogin}
              disabled={loading || !email || !password}
              className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'ログイン中...' : 'ログイン'}
            </button>
            <button
              type="button"
              onClick={() => { clearMessages(); setPhase('forgot'); }}
              className="w-full text-center text-sm text-gray-500 hover:text-blue-600"
            >
              パスワードを忘れた方はこちら
            </button>
          </div>
        )}

        {/* 初回パスワード設定 */}
        {phase === 'new_password' && (
          <div className="mt-6 space-y-4">
            <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-800">
              初回ログインのため、新しいパスワードを設定してください。
            </div>
            <p className="rounded-md border border-blue-100 bg-white px-3 py-2 text-xs leading-relaxed text-gray-600">
              パスワード要件: {PASSWORD_RULE_TEXT}
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700">新しいパスワード</label>
              <PasswordInput
                value={newPassword}
                onChange={setNewPassword}
                onKeyDown={(e) => handleKeyDown(e, handleNewPassword)}
                placeholder="例: Delphi@2026"
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">パスワード確認</label>
              <PasswordInput
                value={newPasswordConfirm}
                onChange={setNewPasswordConfirm}
                onKeyDown={(e) => handleKeyDown(e, handleNewPassword)}
                placeholder="もう一度入力"
                autoComplete="new-password"
              />
            </div>
            <button
              type="button"
              onClick={handleNewPassword}
              disabled={loading || !newPassword || !newPasswordConfirm}
              className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? '設定中...' : 'パスワードを設定してログイン'}
            </button>
          </div>
        )}

        {/* パスワードリセット: メールアドレス入力 */}
        {phase === 'forgot' && (
          <div className="mt-6 space-y-4">
            <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
              登録済みのメールアドレスに確認コードを送信します。
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">メールアドレス</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, handleForgotPassword)}
                placeholder="example@test.com"
                autoComplete="email"
                className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={loading || !email}
              className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? '送信中...' : '確認コードを送信'}
            </button>
            <button
              type="button"
              onClick={() => { clearMessages(); setPhase('login'); }}
              className="w-full text-center text-sm text-gray-500 hover:text-blue-600"
            >
              ログインに戻る
            </button>
          </div>
        )}

        {/* パスワードリセット: 確認コード + 新パスワード */}
        {phase === 'forgot_confirm' && (
          <div className="mt-6 space-y-4">
            <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-800">
              <span className="font-medium">{email}</span> に確認コードを送信しました。
            </div>
            <p className="rounded-md border border-blue-100 bg-white px-3 py-2 text-xs leading-relaxed text-gray-600">
              パスワード要件: {PASSWORD_RULE_TEXT}
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700">確認コード</label>
              <input
                type="text"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                placeholder="メールに届いた6桁のコード"
                autoComplete="one-time-code"
                className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm tracking-widest focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">新しいパスワード</label>
              <PasswordInput
                value={newPassword}
                onChange={setNewPassword}
                placeholder="例: Delphi@2026"
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">パスワード確認</label>
              <PasswordInput
                value={newPasswordConfirm}
                onChange={setNewPasswordConfirm}
                onKeyDown={(e) => handleKeyDown(e, handleConfirmReset)}
                placeholder="もう一度入力"
                autoComplete="new-password"
              />
            </div>
            <button
              type="button"
              onClick={handleConfirmReset}
              disabled={loading || !resetCode || !newPassword || !newPasswordConfirm}
              className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'リセット中...' : 'パスワードをリセット'}
            </button>
            <button
              type="button"
              onClick={() => { clearMessages(); setPhase('login'); }}
              className="w-full text-center text-sm text-gray-500 hover:text-blue-600"
            >
              ログインに戻る
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function translateError(code: string): string {
  switch (code) {
    case 'NotAuthorizedException':
      return 'メールアドレスまたはパスワードが正しくありません';
    case 'UserNotFoundException':
      return 'このメールアドレスは登録されていません';
    case 'UserNotConfirmedException':
      return 'アカウントが確認されていません。管理者に連絡してください';
    case 'PasswordResetRequiredException':
      return 'パスワードのリセットが必要です';
    case 'InvalidPasswordException':
      return `パスワードが要件を満たしていません（${PASSWORD_RULE_TEXT}）`;
    case 'CodeMismatchException':
      return '確認コードが正しくありません';
    case 'ExpiredCodeException':
      return '確認コードの有効期限が切れています。もう一度送信してください';
    case 'LimitExceededException':
      return '試行回数の上限に達しました。しばらく待ってから再試行してください';
    case 'TooManyRequestsException':
      return 'リクエストが多すぎます。しばらく待ってから再試行してください';
    case 'InvalidParameterException':
      return '入力内容に問題があります。メールアドレスを確認してください';
    default:
      return `エラーが発生しました（${code}）`;
  }
}
